/**
 * Copyright © 2026 Adnuntius AS.
 * UI-Router state declarations.
 */
import {Ng2StateDeclaration} from '@uirouter/angular';
import {firstValueFrom, tap} from 'rxjs';

import {PERMISSIONS} from '../../components/auth/auth-constants';
import {UserProfileSaverService} from '../../components/session/user-profile-saver.service';
import {SignalAdminResourceContextActionsComponent} from '../common/controller/signal-admin-resource-context-actions.component';
import {SignalAdminResourcePageState} from '../common/controller/signal-admin-resource-page-state';
import {SignalAdminResourceTitleComponent} from '../common/controller/signal-admin-resource-title.component';
import {OBJECT_TYPE} from '../common/constants/object-type';
import {ListDefaultActionsComponent} from '../common/directives/list-default-actions.component';
import {stateTitleView} from '../common/directives/html/state-title.component';
import {AdnRouterHelperApi, StateDeclarationBuilder} from '../routing/state-utils';
import {CreativeSetApiService, CreativeSetModel} from './creative-set-api.service';
import {CreativeSetCopyModalComponent} from './creative-set-copy-modal.component';
import {creativeSetBreadcrumbData} from './creative-set-info-api.service';
import {CreativeSetItemPageComponent} from './creative-set-item-page.component';
import {CreativeSetListPageComponent} from './creative-set-list-page.component';

export function creativeSetStates(adnRouterHelper: AdnRouterHelperApi): Ng2StateDeclaration[] {
  const states = new StateDeclarationBuilder();
  const api = adnRouterHelper.services.service(CreativeSetApiService);
  const permissions = adnRouterHelper.services.localUserPermissions();
  const userProfileSaver = adnRouterHelper.services.service(UserProfileSaverService);
  const readOnly = () => !permissions.hasAny(PERMISSIONS.manageAdvertising.apiKey)
    && !permissions.hasAny(PERMISSIONS.axAdvertiser.apiKey)
    && !permissions.hasAny(PERMISSIONS.creativeAgency.apiKey);
  const rh = adnRouterHelper.createRouterHelper('creative-sets.creative-set', CreativeSetApiService, 'app.adv', 'app');
  const copyConfig = {
    modalComponent: CreativeSetCopyModalComponent,
    stateUrl: 'app.adv.creative-sets.creative-set',
    lcase: 'creative set',
    ucase: 'Creative Set'
  };

  states.add(rh.listPath(), rh.list({
    resolve: {listReadOnly: readOnly},
    views: {
      'content@app': {component: CreativeSetListPageComponent, bindings: {readOnly: 'listReadOnly'}},
      'context@app': {component: ListDefaultActionsComponent, bindings: {readOnly: 'listReadOnly', newStateName: 'listNewStateName'}},
      'title@app': stateTitleView()
    }
  }));
  states.add(rh.newItemPath(), rh.newItem());
  states.add(rh.itemPath(), rh.item({
    resolve: {
      creativeSetPage: function($stateParams: any, folioFromParam: any, advertiserFromParam: any) {
        let page: Promise<CreativeSetModel> | CreativeSetModel;
        if ($stateParams.isNew) {
          page = {id: $stateParams.id, objectState: 'ACTIVE', validationWarnings: []};
        } else {
          page = firstValueFrom(api.get($stateParams.id).pipe(
            tap((model) => userProfileSaver.saveObj(model, 'adnLastViewed'))
          ));
        }
        return Promise.resolve(page).then((model) => {
          let state: SignalAdminResourcePageState<CreativeSetModel>;
          const crumbParams = ['Advertiser', 'Folio'].map((type) => ({
            type,
            resolve: (data: CreativeSetModel) => {
              const source = state.relatedData().creativeSetBreadcrumbs || data;
              return source?.[OBJECT_TYPE[type].apiParam];
            }
          }));
          state = new SignalAdminResourcePageState(
            model, !!$stateParams.isNew, readOnly(), api, 'Creative Set', 'name', copyConfig, undefined,
            {objectTypeKey: 'CreativeSet', crumbParams}
          );
          state.updateRelatedData('creativeSetBreadcrumbs', creativeSetBreadcrumbData(model, undefined, {
            advertiser: advertiserFromParam,
            folio: folioFromParam
          }));
          return state;
        });
      },
      folioFromParam: function($stateParams: any) {
        return $stateParams.folioId ? {id: $stateParams.folioId, name: $stateParams.folioName} : undefined;
      },
      advertiserFromParam: function($stateParams: any) {
        return $stateParams.advertiserId
          ? {id: $stateParams.advertiserId, name: $stateParams.advertiserName}
          : undefined;
      },
      model: (creativeSetPage: SignalAdminResourcePageState<CreativeSetModel>) => creativeSetPage.model()
    },
    views: {
      'content@app': {
        component: CreativeSetItemPageComponent,
        bindings: {
          state: 'creativeSetPage',
          folioFromParam: 'folioFromParam',
          advertiserFromParam: 'advertiserFromParam'
        }
      },
      'context@app': {component: SignalAdminResourceContextActionsComponent, bindings: {state: 'creativeSetPage'}},
      'title@app': {component: SignalAdminResourceTitleComponent, bindings: {state: 'creativeSetPage'}}
    }
  }));
  return states.build();
}
